import { useState, useEffect, useCallback, useRef } from 'react';
import api, { getErrorMessage } from '../services/api';
import Layout from '../components/Layout';

// ── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const truncate = (str, len = 120) => {
  if (!str) return '';
  return str.length > len ? str.slice(0, len) + '...' : str;
};

// ── Skeleton ─────────────────────────────────────────────────────────────────

const SkeletonCard = () => (
  <div className="bg-white rounded-2xl border border-gray-200 p-6">
    <div className="skeleton h-5 w-3/4 mb-3" />
    <div className="skeleton h-4 w-full mb-2" />
    <div className="skeleton h-4 w-2/3 mb-5" />
    <div className="flex gap-4 mb-4">
      <div className="skeleton h-4 w-20" />
      <div className="skeleton h-4 w-24" />
    </div>
    <div className="skeleton h-3 w-32 mb-4" />
    <div className="flex gap-2">
      <div className="skeleton h-9 w-16 rounded-xl" />
      <div className="skeleton h-9 w-16 rounded-xl" />
    </div>
  </div>
);

const PageSkeleton = () => (
  <Layout>
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="skeleton h-8 w-48" />
        <div className="skeleton h-10 w-32 rounded-xl" />
      </div>
      <div className="grid grid-cols-3 gap-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="bg-white rounded-2xl border border-gray-200 p-4">
            <div className="skeleton h-7 w-12 mb-1" />
            <div className="skeleton h-4 w-24" />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {[...Array(6)].map((_, i) => <SkeletonCard key={i} />)}
      </div>
    </div>
  </Layout>
);

// ── Toast ────────────────────────────────────────────────────────────────────

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const styles = {
    success: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    error: 'bg-red-50 border-red-200 text-red-800',
  };
  const icons = {
    success: 'M5 13l4 4L19 7',
    error: 'M6 18L18 6M6 6l12 12',
  };

  return (
    <div className={`fixed top-20 right-4 z-50 flex items-center gap-3 px-5 py-3 rounded-xl border shadow-lg animate-slide-down ${styles[type]}`}>
      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icons[type]} />
      </svg>
      <span className="text-sm font-medium">{message}</span>
      <button onClick={onClose} className="ml-2 opacity-60 hover:opacity-100">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

// ── Confirm Modal ────────────────────────────────────────────────────────────

const ConfirmModal = ({ title, message, confirmLabel, confirmColor, onConfirm, onCancel, loading }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
    <div className="fixed inset-0 bg-black/40" onClick={onCancel} />
    <div className="relative bg-white rounded-2xl shadow-xl border border-gray-200 p-6 w-full max-w-sm animate-fade-in">
      <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
      <p className="text-sm text-gray-500 mb-6">{message}</p>
      <div className="flex gap-3 justify-end">
        <button
          onClick={onCancel}
          disabled={loading}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={loading}
          className={`px-4 py-2 text-sm font-medium text-white rounded-xl transition-colors disabled:opacity-50 ${confirmColor}`}
        >
          {loading ? 'Deleting...' : confirmLabel}
        </button>
      </div>
    </div>
  </div>
);

// ── Topics & Subtopics Section (used inside edit modal) ──────────────────────

const TopicsSection = ({ courseId, showToast }) => {
  const [topics, setTopics]             = useState([]);
  const [tLoading, setTLoading]         = useState(true);
  const [mutating, setMutating]         = useState(false);
  const [newTopicTitle, setNewTopicTitle] = useState('');
  const [editingTopic, setEditingTopic] = useState(null); // { id, title }
  const [editingSub, setEditingSub]     = useState(null); // { id, title }
  const [addSubFor, setAddSubFor]       = useState(null); // topic id
  const [newSubTitle, setNewSubTitle]   = useState('');

  const refresh = useCallback(async () => {
    try {
      const res = await api.get(`/api/progress/courses/${courseId}/topics`);
      setTopics(res.data || []);
    } catch {
      showToast('Failed to load topics', 'error');
    } finally {
      setTLoading(false);
    }
  }, [courseId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { refresh(); }, [refresh]);

  const mutate = async (fn, successMsg) => {
    setMutating(true);
    try {
      await fn();
      showToast(successMsg);
      refresh();
    } catch {
      showToast('Action failed', 'error');
    } finally {
      setMutating(false);
    }
  };

  const addTopic = () => {
    if (!newTopicTitle.trim()) return;
    mutate(
      () => api.post(`/api/progress/courses/${courseId}/topics`, { title: newTopicTitle.trim(), order: topics.length + 1 }),
      'Topic added'
    );
    setNewTopicTitle('');
  };

  const saveTopic = () => {
    if (!editingTopic?.title.trim()) return;
    mutate(
      () => api.put(`/api/progress/topics/${editingTopic.id}`, { title: editingTopic.title.trim() }),
      'Topic updated'
    );
    setEditingTopic(null);
  };

  const addSubtopic = (topicId) => {
    if (!newSubTitle.trim()) return;
    const topic = topics.find(t => t.id === topicId);
    const order = (topic?.subtopics?.length || 0) + 1;
    mutate(
      () => api.post(`/api/progress/topics/${topicId}/subtopics`, { title: newSubTitle.trim(), order }),
      'Subtopic added'
    );
    setNewSubTitle('');
    setAddSubFor(null);
  };

  const saveSub = () => {
    if (!editingSub?.title.trim()) return;
    mutate(
      () => api.put(`/api/progress/subtopics/${editingSub.id}`, { title: editingSub.title.trim() }),
      'Subtopic updated'
    );
    setEditingSub(null);
  };

  if (tLoading) return (
    <div className="px-6 pb-6 space-y-2">
      {[1, 2, 3].map(i => <div key={i} className="h-10 bg-gray-100 rounded-xl animate-pulse" />)}
    </div>
  );

  return (
    <div className="px-6 pb-6 space-y-3">
      {topics.length === 0 ? (
        <p className="text-xs text-gray-400 text-center py-3">No topics yet — add one below.</p>
      ) : (
        <div className="space-y-2">
          {topics.map((topic, idx) => (
            <div key={topic.id} className="border border-gray-200 rounded-xl overflow-hidden">
              {/* Topic row */}
              <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-100">
                <div
                  className="w-5 h-5 rounded-md flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0"
                  style={{ background: '#003479' }}
                >{idx + 1}</div>

                {editingTopic?.id === topic.id ? (
                  <>
                    <input
                      autoFocus
                      value={editingTopic.title}
                      onChange={e => setEditingTopic({ ...editingTopic, title: e.target.value })}
                      onKeyDown={e => { if (e.key === 'Enter') saveTopic(); if (e.key === 'Escape') setEditingTopic(null); }}
                      className="flex-1 px-2 py-0.5 text-sm border border-gray-300 rounded-lg outline-none focus:border-primary min-w-0"
                    />
                    <button onClick={saveTopic} disabled={mutating} className="text-xs text-white px-2 py-0.5 rounded-lg bg-primary">Save</button>
                    <button onClick={() => setEditingTopic(null)} className="text-xs text-gray-400 hover:text-gray-700 px-1">✕</button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 text-sm font-medium text-gray-800 min-w-0 truncate">{topic.title}</span>
                    <button
                      onClick={() => { setEditingTopic({ id: topic.id, title: topic.title }); setEditingSub(null); }}
                      className="p-1 text-gray-400 hover:text-gray-700 rounded hover:bg-white transition-colors"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => mutate(() => api.delete(`/api/progress/topics/${topic.id}`), 'Topic deleted')}
                      disabled={mutating}
                      className="p-1 text-red-300 hover:text-red-600 rounded hover:bg-red-50 transition-colors"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </>
                )}
              </div>

              {/* Subtopics */}
              <div className="px-3 py-2 space-y-1.5 bg-white">
                {topic.subtopics?.map(sub => (
                  <div key={sub.id} className="flex items-center gap-2 pl-4">
                    <span className="text-gray-300 text-xs flex-shrink-0">└</span>
                    {editingSub?.id === sub.id ? (
                      <>
                        <input
                          autoFocus
                          value={editingSub.title}
                          onChange={e => setEditingSub({ ...editingSub, title: e.target.value })}
                          onKeyDown={e => { if (e.key === 'Enter') saveSub(); if (e.key === 'Escape') setEditingSub(null); }}
                          className="flex-1 px-2 py-0.5 text-xs border border-gray-300 rounded-lg outline-none focus:border-primary min-w-0"
                        />
                        <button onClick={saveSub} disabled={mutating} className="text-[10px] text-white px-1.5 py-0.5 rounded-md bg-primary">Save</button>
                        <button onClick={() => setEditingSub(null)} className="text-[10px] text-gray-400 hover:text-gray-700 px-1">✕</button>
                      </>
                    ) : (
                      <>
                        <span className="flex-1 text-xs text-gray-600 min-w-0 truncate">{sub.title}</span>
                        <button
                          onClick={() => { setEditingSub({ id: sub.id, title: sub.title }); setEditingTopic(null); }}
                          className="p-0.5 text-gray-300 hover:text-gray-600 transition-colors"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => mutate(() => api.delete(`/api/progress/subtopics/${sub.id}`), 'Subtopic deleted')}
                          disabled={mutating}
                          className="p-0.5 text-red-300 hover:text-red-500 transition-colors"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </>
                    )}
                  </div>
                ))}

                {/* Inline add subtopic */}
                {addSubFor === topic.id ? (
                  <div className="flex items-center gap-2 pl-4">
                    <span className="text-gray-300 text-xs flex-shrink-0">└</span>
                    <input
                      autoFocus
                      value={newSubTitle}
                      onChange={e => setNewSubTitle(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') addSubtopic(topic.id); if (e.key === 'Escape') { setAddSubFor(null); setNewSubTitle(''); } }}
                      placeholder="Subtopic title..."
                      className="flex-1 px-2 py-0.5 text-xs border border-gray-300 rounded-lg outline-none focus:border-primary min-w-0"
                    />
                    <button onClick={() => addSubtopic(topic.id)} disabled={mutating} className="text-[10px] text-white px-1.5 py-0.5 rounded-md bg-primary">Add</button>
                    <button onClick={() => { setAddSubFor(null); setNewSubTitle(''); }} className="text-[10px] text-gray-400 hover:text-gray-700 px-1">✕</button>
                  </div>
                ) : (
                  <button
                    onClick={() => { setAddSubFor(topic.id); setNewSubTitle(''); }}
                    className="pl-4 flex items-center gap-1 text-xs text-gray-400 hover:text-primary transition-colors"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add subtopic
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add topic input */}
      <div className="flex items-center gap-2 pt-1">
        <input
          type="text"
          value={newTopicTitle}
          onChange={e => setNewTopicTitle(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') addTopic(); }}
          placeholder="New topic title..."
          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
        />
        <button
          onClick={addTopic}
          disabled={mutating || !newTopicTitle.trim()}
          className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-xl hover:bg-primary/90 transition-colors disabled:opacity-50 whitespace-nowrap"
        >
          Add Topic
        </button>
      </div>
    </div>
  );
};

// ── Course Form Modal ────────────────────────────────────────────────────────

const CourseFormModal = ({ course, onSave, onCancel, loading, showToast }) => {
  const isEdit = !!course;
  const [form, setForm] = useState({
    title: course?.title || '',
    description: course?.description || '',
  });
  const [formError, setFormError] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.title.trim()) return setFormError('Course title is required.');
    onSave(form);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/40" onClick={onCancel} />
      <div
        className={`relative bg-white rounded-2xl shadow-xl border border-gray-200 w-full animate-fade-in flex flex-col ${isEdit ? 'max-w-2xl' : 'max-w-lg'}`}
        style={{ maxHeight: '90vh' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 flex-shrink-0">
          <h3 className="text-lg font-bold text-gray-900">{isEdit ? 'Edit Course' : 'Add New Course'}</h3>
          <button onClick={onCancel} className="p-1 text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1">
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {formError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{formError}</div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Course Title</label>
              <input
                ref={inputRef}
                type="text"
                value={form.title}
                onChange={(e) => { setForm({ ...form, title: e.target.value }); setFormError(''); }}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm"
                placeholder="e.g. Full Stack Web Development"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={isEdit ? 3 : 4}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm resize-none"
                placeholder="Brief description of the course content, objectives, and target audience..."
              />
            </div>

            <div className="flex gap-3 justify-end pt-2">
              <button
                type="button"
                onClick={onCancel}
                disabled={loading}
                className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-5 py-2.5 text-sm font-medium text-white bg-primary rounded-xl hover:bg-primary/90 transition-colors disabled:opacity-50"
              >
                {loading ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Course'}
              </button>
            </div>
          </form>

          {/* Topics & Subtopics — edit mode only */}
          {isEdit && (
            <>
              <div className="border-t border-gray-100 mx-6" />
              <div className="px-6 pt-4 pb-2 flex items-center gap-2">
                <div className="w-1 h-5 rounded-full bg-primary" />
                <h4 className="text-sm font-bold text-gray-900">Topics &amp; Subtopics</h4>
                <p className="text-xs text-gray-400 ml-1">Trainers use these to rate students</p>
              </div>
              <TopicsSection courseId={course.id} showToast={showToast} />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Main Component ───────────────────────────────────────────────────────────

const CourseManagement = () => {
  const [courses, setCourses] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  // Modals
  const [showForm, setShowForm] = useState(false);
  const [editCourse, setEditCourse] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  // Toast
  const [toast, setToast] = useState(null);
  const showToast = (message, type = 'success') => setToast({ message, type });

  // ── Fetch ──────────────────────────────────────────────────────────────

  const fetchCourses = useCallback(async () => {
    try {
      const res = await api.get('/api/courses');
      setCourses(res.data || []);
      setError('');
    } catch {
      setError('Failed to load courses.');
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/api/courses/stats');
      setStats(res.data);
    } catch {
      // non-critical
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchCourses(), fetchStats()]);
      setLoading(false);
    };
    load();
  }, [fetchCourses, fetchStats]);

  // ── Filtered list ──────────────────────────────────────────────────────

  const filtered = search.trim()
    ? courses.filter(
        (c) =>
          c.title.toLowerCase().includes(search.toLowerCase()) ||
          (c.description || '').toLowerCase().includes(search.toLowerCase())
      )
    : courses;

  // ── Actions ────────────────────────────────────────────────────────────

  const handleCreate = async (form) => {
    setModalLoading(true);
    try {
      await api.post('/api/courses', {
        title: form.title,
        description: form.description || null,
      });
      setShowForm(false);
      showToast('Course created successfully');
      fetchCourses();
      fetchStats();
    } catch (err) {
      showToast(getErrorMessage(err, 'Failed to create course'), 'error');
    } finally {
      setModalLoading(false);
    }
  };

  const handleEdit = async (form) => {
    setModalLoading(true);
    try {
      await api.put(`/api/courses/${editCourse.id}`, {
        title: form.title,
        description: form.description || null,
      });
      setEditCourse(null);
      showToast('Course updated successfully');
      fetchCourses();
    } catch (err) {
      showToast(getErrorMessage(err, 'Failed to update course'), 'error');
    } finally {
      setModalLoading(false);
    }
  };

  const handleDelete = async () => {
    setModalLoading(true);
    try {
      await api.delete(`/api/courses/${deleteTarget.id}`);
      setDeleteTarget(null);
      showToast('Course deleted successfully');
      fetchCourses();
      fetchStats();
    } catch (err) {
      showToast(getErrorMessage(err, 'Failed to delete course'), 'error');
    } finally {
      setModalLoading(false);
    }
  };

  // ── Loading / Error ────────────────────────────────────────────────────

  if (loading) return <PageSkeleton />;

  if (error && !courses.length) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-32 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-sm border border-red-200 p-8 max-w-md w-full text-center">
            <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Failed to load courses</h2>
            <p className="text-gray-500 mb-6">{error}</p>
            <button onClick={() => window.location.reload()} className="px-6 py-2 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors font-medium">
              Retry
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <Layout>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="space-y-6 animate-fade-in">
        {/* ── Header ────────────────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Course Management</h1>
            <p className="text-sm text-gray-500 mt-1">Create and manage your courses</p>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-white text-sm font-semibold rounded-xl hover:bg-primary/90 transition-colors shadow-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Course
          </button>
        </div>

        {/* ── Stats ─────────────────────────────────────────────────── */}
        {stats && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { label: 'Total Courses', value: stats.total_courses, icon: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253', color: 'from-violet-500 to-purple-600' },
              { label: 'Total Batches', value: stats.total_batches, icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10', color: 'from-blue-500 to-indigo-600' },
              { label: 'Enrolled Students', value: stats.total_enrolled, icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z', color: 'from-emerald-500 to-teal-600' },
            ].map((s) => (
              <div key={s.label} className={`bg-gradient-to-br ${s.color} rounded-2xl p-5 text-white shadow-lg`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-3xl font-bold">{(s.value ?? 0).toLocaleString()}</p>
                    <p className="text-sm opacity-90 mt-0.5">{s.label}</p>
                  </div>
                  <div className="w-11 h-11 bg-white/20 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={s.icon} />
                    </svg>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Search ────────────────────────────────────────────────── */}
        <div className="relative max-w-md">
          <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search courses..."
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm"
          />
        </div>

        {/* ── Course Grid ───────────────────────────────────────────── */}
        {filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
            <div className="flex flex-col items-center justify-center py-20 text-gray-400">
              <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
                <svg className="w-7 h-7 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <p className="text-sm font-medium text-gray-500">
                {search ? 'No courses match your search' : 'No courses yet'}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {search ? 'Try a different keyword' : 'Create your first course to get started'}
              </p>
              {!search && (
                <button
                  onClick={() => setShowForm(true)}
                  className="mt-5 px-5 py-2 bg-primary/10 text-primary text-sm font-medium rounded-xl hover:bg-primary/20 transition-colors"
                >
                  Create Course
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((course) => (
              <div
                key={course.id}
                className="bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md hover:scale-[1.02] transition-all duration-200 flex flex-col overflow-hidden group"
              >
                {/* Color bar */}
                <div className="h-1.5 bg-gradient-to-r from-primary to-secondary" />

                <div className="p-6 flex flex-col flex-1">
                  {/* Title */}
                  <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-primary transition-colors">
                    {course.title}
                  </h3>

                  {/* Description */}
                  <p className="text-sm text-gray-500 mb-4 flex-1 leading-relaxed">
                    {truncate(course.description) || (
                      <span className="italic text-gray-400">No description provided</span>
                    )}
                  </p>

                  {/* Stats chips */}
                  <div className="flex items-center gap-3 mb-4">
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-700 bg-blue-50 px-2.5 py-1 rounded-full">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                      {course.batch_count ?? 0} {course.batch_count === 1 ? 'Batch' : 'Batches'}
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                      {course.student_count ?? 0} Students
                    </span>
                  </div>

                  {/* Meta */}
                  <div className="flex items-center gap-2 text-xs text-gray-400 mb-5">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span>{course.creator_name || 'Admin'}</span>
                    <span className="text-gray-300">&middot;</span>
                    <span>{formatDate(course.created_at)}</span>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 pt-4 border-t border-gray-100">
                    <button
                      onClick={() => setEditCourse(course)}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      Edit
                    </button>
                    <button
                      onClick={() => setDeleteTarget(course)}
                      className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-medium text-red-600 bg-red-50 rounded-xl hover:bg-red-100 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Modals ───────────────────────────────────────────────────── */}
      {showForm && (
        <CourseFormModal
          course={null}
          onSave={handleCreate}
          onCancel={() => setShowForm(false)}
          loading={modalLoading}
        />
      )}

      {editCourse && (
        <CourseFormModal
          course={editCourse}
          onSave={handleEdit}
          onCancel={() => setEditCourse(null)}
          loading={modalLoading}
          showToast={showToast}
        />
      )}

      {deleteTarget && (
        <ConfirmModal
          title="Delete Course"
          message={`Are you sure you want to delete "${deleteTarget.title}"? All batches and enrollments under this course will also be removed. This cannot be undone.`}
          confirmLabel="Delete"
          confirmColor="bg-red-600 hover:bg-red-700"
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
          loading={modalLoading}
        />
      )}
    </Layout>
  );
};

export default CourseManagement;
